import {
  calculateAchievements,
  getAchievementExperience
} from "./achievements";
import { calculateExperience } from "./levelSystem";
import { buildRatingShareStats } from "./publicProfileStats";
import { RATINGS_SYNC_VERSION } from "./ratingsSync";
import { getAchievementExperienceComponentId } from "./gameExperienceLedger";
import { getServerExperience } from "./serverExperience";
import { requestExperienceSettlement } from "./experienceSettlementApi";
import type { Rating } from "../types/rating";
import type { AchievementGroups, ExperienceEventInput, ExperienceMutationComponent } from "../types/experience";
import type { DateValue } from "../types/data";
import { isUnknownArray, isRecord } from "../types/data";
import type { PublicStats } from "../types/profile";
import type { ExperienceSettlementOptions, ExperienceSourceSummary } from "./experienceSettlementApi";
import type firebaseSdk from "firebase/compat/app";
import "firebase/compat/firestore";

export interface ImportDirectMutation {
  id: string;
  filmId: string;
  action: "set" | "delete";
  lifecycleId: string;
  previousLifecycleId: string;
  previousDeleteApplied: boolean;
  delta: number;
}
export type ImportDirectMutationInput = Omit<ImportDirectMutation, "previousLifecycleId" | "previousDeleteApplied"> & Partial<Pick<ImportDirectMutation, "previousLifecycleId" | "previousDeleteApplied">>;
interface ImportRatingShareStats {
  watchedFilms: number;
  countriesCount: number;
  favoriteGenre: string;
  averageRating: number | null;
}
export interface ImportExperienceCheckpoint {
  version: number;
  id: string;
  operationId: string;
  batchIndex: number;
  directDelta: number;
  directMutations: ImportDirectMutation[];
  components: ExperienceMutationComponent[];
  watchedFilmsDelta: number;
  beforeWatchedFilms: number;
  changedRatingsCount: number;
  importedCount: number;
  deletedCount: number;
  ratingShareStats: ImportRatingShareStats;
  createdAt?: DateValue;
}
export interface ImportExperienceSettlementResult {
  applied: boolean;
  reason?: string;
  appliedCount?: number;
  pendingCount?: number;
  pendingReason?: string;
  experienceDelta?: number;
  previousLevel?: number | null;
  nextLevel?: number | null;
  publicStats?: PublicStats | null;
  events: ExperienceEventInput[];
  sourceSummary?: ExperienceSourceSummary;
}
type ImportSettlementOptions = Pick<ExperienceSettlementOptions, "firestore" | "firebase" | "user">;
interface BaselineOptions { firestore?: firebaseSdk.firestore.Firestore | null; userId?: string; }

export const IMPORT_EXPERIENCE_OUTBOX_VERSION = 1;
export const IMPORT_EXPERIENCE_CHECKPOINT_LIMIT = 100;

const MAX_COMPONENTS = 256;
const MAX_DELTA = 1_000_000_000;

const snapshotExists = (snapshot: { exists: boolean | (() => boolean) } | null | undefined) =>
  typeof snapshot?.exists === "function" ? snapshot.exists() : Boolean(snapshot?.exists);

const normalizeDelta = (value: unknown) => {
  const delta = Number(value);
  return Number.isFinite(delta) && Math.abs(delta) <= MAX_DELTA
    ? delta
    : null;
};

const normalizeComponents = (value: unknown): ExperienceMutationComponent[] | null => {
  if (!isUnknownArray(value) || value.length > MAX_COMPONENTS) return null;
  const seen = new Set<string>();
  const components: ExperienceMutationComponent[] = [];

  for (const item of value) {
    if (!isRecord(item)) return null;
    const id = String(item?.id || "").trim();
    const delta = normalizeDelta(item?.delta);
    if (!id || id.length > 500 || delta === null || delta === 0 || seen.has(id)) {
      return null;
    }
    seen.add(id);
    components.push({ id, delta });
  }

  return components;
};

const normalizeDirectMutations = (value: unknown): ImportDirectMutation[] | null => {
  if (!isUnknownArray(value) || value.length > MAX_COMPONENTS) return null;
  const mutations: ImportDirectMutation[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (!isRecord(item)) return null;
    const id = String(item?.id || "").trim();
    const filmId = String(item?.filmId || "").trim();
    const action = String(item?.action || "").trim();
    const lifecycleId = String(item?.lifecycleId || "").trim();
    const previousLifecycleId = String(item?.previousLifecycleId || "initial").trim();
    const previousDeleteApplied = item?.previousDeleteApplied === true;
    const delta = normalizeDelta(item?.delta);
    if (
      !id || id.length > 500 || !filmId || filmId.length > 120 ||
      (action !== "set" && action !== "delete") || !lifecycleId ||
      !previousLifecycleId || previousLifecycleId.length > 500 ||
      delta === null || delta === 0 || seen.has(id)
    ) {
      return null;
    }
    seen.add(id);
    mutations.push({
      id,
      filmId,
      action,
      lifecycleId,
      previousLifecycleId,
      previousDeleteApplied,
      delta
    });
  }
  return mutations;
};

const normalizeShareStats = (value: unknown = {}): ImportRatingShareStats | null => {
  if (!isRecord(value)) return null;
  const watchedFilms = Number(value.watchedFilms);
  const countriesCount = Number(value.countriesCount);
  const averageRating = value.averageRating == null
    ? null
    : Number(value.averageRating);
  const favoriteGenre = String(value.favoriteGenre || "");
  if (
    !Number.isFinite(watchedFilms) || watchedFilms < 0 ||
    !Number.isFinite(countriesCount) || countriesCount < 0 ||
    (averageRating != null && (!Number.isFinite(averageRating) || averageRating < 0 || averageRating > 10))
  ) {
    return null;
  }
  return {
    watchedFilms,
    countriesCount,
    favoriteGenre,
    averageRating
  };
};

export const normalizeImportExperienceCheckpoint = (value: unknown, fallbackId = ""): ImportExperienceCheckpoint | null => {
  if (!isRecord(value)) return null;
  const version = Number(value.version);
  const id = String(value.id || fallbackId || "").trim();
  const operationId = String(value.operationId || "").trim();
  const batchIndex = Number(value.batchIndex);
  const directDelta = normalizeDelta(value.directDelta);
  const directMutations = normalizeDirectMutations(value.directMutations);
  const components = normalizeComponents(value.components);
  const watchedFilmsDelta = normalizeDelta(value.watchedFilmsDelta);
  const beforeWatchedFilms = Number(value.beforeWatchedFilms);
  const changedRatingsCount = Number(value.changedRatingsCount);
  const importedCount = Number(value.importedCount || 0);
  const deletedCount = Number(value.deletedCount || 0);
  const ratingShareStats = normalizeShareStats(value.ratingShareStats);

  if (
    version !== IMPORT_EXPERIENCE_OUTBOX_VERSION ||
    !id || id.length > 300 || !/^[a-zA-Z0-9_-]+$/.test(id) ||
    !operationId || operationId.length > 200 ||
    !Number.isInteger(batchIndex) || batchIndex < 0 ||
    directDelta === null || directMutations === null || components === null || watchedFilmsDelta === null ||
    directMutations.reduce((sum, mutation) => sum + mutation.delta, 0) !== directDelta ||
    !Number.isFinite(beforeWatchedFilms) || beforeWatchedFilms < 0 ||
    !Number.isInteger(changedRatingsCount) || changedRatingsCount <= 0 ||
    !Number.isInteger(importedCount) || importedCount < 0 ||
    !Number.isInteger(deletedCount) || deletedCount < 0 ||
    ratingShareStats === null
  ) {
    return null;
  }

  return {
    version,
    id,
    operationId,
    batchIndex,
    directDelta,
    directMutations,
    components,
    watchedFilmsDelta,
    beforeWatchedFilms,
    changedRatingsCount,
    importedCount,
    deletedCount,
    ratingShareStats,
    ...(value.createdAt != null ? { createdAt: value.createdAt as DateValue } : {})
  };
};

const getUnlockedAchievements = (achievementCategories: AchievementGroups = {}) =>
  Object.values(achievementCategories)
    .flat()
    .filter((achievement) => achievement?.unlocked)
    .map((achievement): [string, number] => [
      getAchievementExperienceComponentId(achievement),
      getAchievementExperience(achievement)
    ]);

const getAchievementComponentChanges = (beforeRatings: Rating[], afterRatings: Rating[]) => {
  const before = new Map(getUnlockedAchievements(calculateAchievements(beforeRatings)));
  const after = new Map(getUnlockedAchievements(calculateAchievements(afterRatings)));
  return [...new Set([...before.keys(), ...after.keys()])]
    .map((id) => ({
      id,
      delta: (after.get(id) || 0) - (before.get(id) || 0)
    }))
    .filter((component) => component.delta !== 0);
};

const safeOperationPart = (value: string | undefined) =>
  String(value || "")
    .replace(/[^a-zA-Z0-9_-]/g, "_")
    .slice(0, 180);

export const createImportExperienceOperationId = () => {
  const randomId = typeof window !== "undefined"
    ? window.crypto?.randomUUID?.()
    : null;
  if (randomId) return `op_${safeOperationPart(randomId)}`;
  return `op_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
};

export const createImportExperienceCheckpoint = ({
  operationId,
  batchIndex,
  beforeRatings = [],
  afterRatings = [],
  changedRatingsCount,
  importedCount = 0,
  deletedCount = 0,
  directMutations = [],
  createdAt = null
}: { operationId?: string; batchIndex?: number; beforeRatings?: Rating[]; afterRatings?: Rating[]; changedRatingsCount?: number; importedCount?: number; deletedCount?: number; directMutations?: ImportDirectMutationInput[]; createdAt?: DateValue } = {}) => {
  const normalizedOperationId = safeOperationPart(operationId);
  const normalizedBatchIndex = Number(batchIndex);
  const id = `import_${normalizedOperationId}_${String(normalizedBatchIndex).padStart(6, "0")}`;
  const beforeShareStats = buildRatingShareStats(beforeRatings);
  const afterShareStats = buildRatingShareStats(afterRatings);
  const calculatedDirectDelta =
    calculateExperience(afterRatings, {}) -
    calculateExperience(beforeRatings, {});
  const checkpoint = normalizeImportExperienceCheckpoint({
    version: IMPORT_EXPERIENCE_OUTBOX_VERSION,
    id,
    operationId: normalizedOperationId,
    batchIndex: normalizedBatchIndex,
    directDelta: calculatedDirectDelta,
    directMutations,
    components: getAchievementComponentChanges(beforeRatings, afterRatings),
    watchedFilmsDelta:
      afterShareStats.watchedFilms - beforeShareStats.watchedFilms,
    beforeWatchedFilms: beforeShareStats.watchedFilms,
    changedRatingsCount,
    importedCount,
    deletedCount,
    ratingShareStats: afterShareStats,
    ...(createdAt != null ? { createdAt } : {})
  });
  if (!checkpoint) throw new TypeError("Invalid import experience checkpoint");
  return checkpoint;
};

/** Shared write operation implemented by both a Firestore batch and a transaction. */
interface ImportCheckpointWriter {
  set(reference: firebaseSdk.firestore.DocumentReference, data: Record<string, unknown>, options: firebaseSdk.firestore.SetOptions): unknown;
}

export const stageImportExperienceCheckpoint = ({
  batch,
  firestore,
  firebase,
  userId,
  checkpoint: value,
  ratingsRevision = null,
  ratingsCount = null
}: { batch?: ImportCheckpointWriter; firestore?: firebaseSdk.firestore.Firestore; firebase?: Pick<typeof firebaseSdk, "firestore">; userId?: string; checkpoint?: ImportExperienceCheckpoint | null; ratingsRevision?: number | null; ratingsCount?: number | null } = {}) => {
  const checkpoint = normalizeImportExperienceCheckpoint(value, value?.id);
  const normalizedUserId = String(userId || "").trim();
  if (!batch || !firestore || !normalizedUserId || !checkpoint) {
    throw new TypeError("Invalid import experience checkpoint target");
  }
  const rootRef = firestore.collection("userNotifications").doc(normalizedUserId);
  batch.set(rootRef, {
    importExperienceOutbox: {
      version: IMPORT_EXPERIENCE_OUTBOX_VERSION,
      checkpoints: {
        [checkpoint.id]: checkpoint
      }
    }
  }, { merge: true });
  if (ratingsRevision != null || ratingsCount != null) {
    const normalizedRevision = Number(ratingsRevision);
    const normalizedRatingsCount = Number(ratingsCount);
    if (
      !firebase ||
      !Number.isInteger(normalizedRevision) || normalizedRevision <= 0 ||
      !Number.isInteger(normalizedRatingsCount) || normalizedRatingsCount < 0
    ) {
      throw new TypeError("Invalid import ratings revision checkpoint");
    }
    batch.set(firestore.collection("users").doc(normalizedUserId), {
      ratingsSync: {
        version: RATINGS_SYNC_VERSION,
        revision: normalizedRevision,
        ratingsCount: normalizedRatingsCount,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp()
      }
    }, { merge: true });
  }
  return checkpoint;
};

export const getTrustedImportExperienceBaseline = async ({
  firestore,
  userId
}: BaselineOptions = {}) => {
  const normalizedUserId = String(userId || "").trim();
  if (!firestore || !normalizedUserId) return null;
  const snapshot = await firestore
    .collection("users")
    .doc(normalizedUserId)
    .get({ source: "server" });
  if (!snapshotExists(snapshot)) return null;
  const profile: { publicStats?: PublicStats; ratingsSync?: { revision?: number } } = snapshot.data() || {};
  const totalExperience = getServerExperience(
    profile.publicStats?.totalExperience
  );
  const rawRevision = Number(profile.ratingsSync?.revision);
  if (totalExperience === null) return null;
  return {
    totalExperience,
    ratingsRevision:
      Number.isInteger(rawRevision) && rawRevision > 0 ? rawRevision : 0
  };
};

export const hasTrustedImportExperienceBaseline = async (options: BaselineOptions = {}) =>
  (await getTrustedImportExperienceBaseline(options)) !== null;

export const settlePendingImportExperience = async ({
  firestore,
  firebase,
  user
}: ImportSettlementOptions = {}): Promise<ImportExperienceSettlementResult> => {
  const userId = String(user?.uid || "").trim();
  if (!firestore || !firebase || !userId) {
    return { applied: false, reason: "invalid-target", events: [] };
  }
  const settlement = await requestExperienceSettlement({
    firestore,
    firebase,
    user,
    userId,
    reason: "ratings-import"
  });
  return {
    ...settlement,
    applied: Boolean(settlement?.publicStats),
    appliedCount: settlement?.publicStats ? 1 : 0,
    pendingCount: 0,
    previousLevel: null,
    nextLevel: settlement?.publicStats?.level ?? null,
    events: []
  };
};
export const settleAllPendingImportExperience = async (options: ImportSettlementOptions = {}): Promise<ImportExperienceSettlementResult> => {
  const aggregate: ImportExperienceSettlementResult & { appliedCount: number; experienceDelta: number } = {
    applied: false,
    appliedCount: 0,
    experienceDelta: 0,
    previousLevel: null,
    nextLevel: null,
    publicStats: null,
    events: []
  };
  for (let index = 0; index < 20; index += 1) {
    const result = await settlePendingImportExperience(options);
    if (!result?.applied) {
      return aggregate.applied ? { ...aggregate, pendingReason: result?.reason } : result;
    }
    aggregate.applied = true;
    aggregate.appliedCount += result.appliedCount || 0;
    aggregate.experienceDelta += result.experienceDelta || 0;
    aggregate.previousLevel ??= result.previousLevel;
    aggregate.nextLevel = result.nextLevel;
    aggregate.publicStats = result.publicStats;
    aggregate.events.push(...(result.events || []));
    if (!result.pendingCount) return aggregate;
  }
  return { ...aggregate, pendingReason: "iteration-limit" };
};
